// src/utils/qzReceiptPrinter.js
// TKT-2026-103353: silent auto-printing of the kiosk collection receipt via QZ Tray,
// mirroring the POS QZ Tray integration. Reuses the shared signing endpoints on the
// main API (api.sortswift.com/api/qz/*), which allow any origin, so the kiosk needs no
// certificate or private key of its own.
//
// TKT-2026-108319: the certificate is retried, and a kiosk that connected to QZ Tray
// without it is reported and re-signed once it loads (the POS fix, sortswift-pos-main
// #957). qz-tray 2.2.4 fails open: when the certificate promise rejects it connects
// with `certificate: null`, and that socket stays anonymous for its whole life, so QZ
// Tray asks Allow on every receipt and greys out Remember.

import { reportDiagnosticEvent } from './error-reporter';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.sortswift.com/api';
const PRINTER_KEY = 'kioskReceiptPrinter';

// The report's own words. They must not contain what the reporter's ignore list
// drops ('Network Error', 'Failed to fetch'); the request's error rides in details.
export const QZ_UNSIGNED_REPORT = '[QZ] Certificate unavailable, kiosk connected to QZ Tray unsigned';
export const QZ_RESIGN_INTERVAL_MS = 60_000;

const fetchQzCertificate = () =>
    fetch(`${API_URL}/qz/certificate`, {
        headers: { 'Cache-Control': 'no-store' },
        ...(typeof AbortSignal !== 'undefined' && AbortSignal.timeout ? { signal: AbortSignal.timeout(5000) } : {}),
    });

// The certificate, retried, and only when it IS one: a 200 that is not a PEM (a proxy
// or challenge page) counts as a failure, since QZ Tray would take it and still not
// trust the kiosk. Rejects with the last failure.
export async function loadQzCertificate({
    attempts = 3,
    delays = [300, 900],
    fetchCert = fetchQzCertificate,
    sleep = (ms) => new Promise((r) => setTimeout(r, ms)),
} = {}) {
    let lastErr = null;
    for (let i = 0; i < attempts; i += 1) {
        try {
            // eslint-disable-next-line no-await-in-loop
            const res = await fetchCert();
            // eslint-disable-next-line no-await-in-loop
            const text = res && res.ok ? await res.text() : null;
            if (typeof text === 'string' && text.includes('-----BEGIN CERTIFICATE-----')) return text;
            lastErr = new Error(
                res && res.ok
                    ? 'Certificate load failed: the response is not a certificate'
                    : `Certificate load failed: ${res && res.status}`
            );
            lastErr.status = res && res.status;
        } catch (err) {
            lastErr = err;
        }
        // eslint-disable-next-line no-await-in-loop
        if (i < attempts - 1) await sleep(delays[Math.min(i, delays.length - 1)]);
    }
    throw lastErr;
}

// null: no connect has asked for the certificate yet; true: the socket carries it;
// false: QZ Tray was connected without it.
let _qzSocketSigned = null;
let _qzResignTriedAt = 0;
let _qzConnecting = null;

// qz-tray relies on the browser. Lazy-require and guard so a missing module or a
// non-browser context never breaks the app.
let qz = null;
if (typeof window !== 'undefined') {
    try {
        // eslint-disable-next-line global-require
        qz = require('qz-tray');
        qz = qz.default || qz;
        qz.security.setSignatureAlgorithm('SHA256');
        // Never rejectOnFailure: a kiosk without the certificate still prints its
        // receipt, through an anonymous socket that asks Allow each time.
        qz.security.setCertificatePromise((resolve, reject) => {
            loadQzCertificate()
                .then((cert) => {
                    _qzSocketSigned = true;
                    resolve(cert);
                })
                .catch((err) => {
                    _qzSocketSigned = false;
                    reportDiagnosticEvent(QZ_UNSIGNED_REPORT, {
                        reason: String((err && err.message) || err),
                        status: err && err.status,
                        name: err && err.name,
                    });
                    reject(err);
                });
        });
        qz.security.setSignaturePromise((toSign) => (resolve, reject) => {
            fetch(`${API_URL}/qz/sign?request=${encodeURIComponent(toSign)}`, { headers: { 'Cache-Control': 'no-store' } })
                .then((r) => (r.ok ? r.text() : Promise.reject(new Error(`Signature endpoint error: ${r.status}`))))
                .then((t) => resolve(String(t).trim()))
                .catch(reject);
        });
    } catch (e) {
        qz = null;
    }
}

export function isQZLoaded() {
    return !!(qz && qz.websocket);
}

export function getKioskReceiptPrinter() {
    if (typeof window === 'undefined') return '';
    return localStorage.getItem(PRINTER_KEY) || '';
}

export function setKioskReceiptPrinter(name) {
    if (typeof window === 'undefined') return;
    if (name) localStorage.setItem(PRINTER_KEY, name);
    else localStorage.removeItem(PRINTER_KEY);
}

// One connect at a time for the whole page: the warm-up and a checkout print both
// connect, and qz-tray refuses a second connect() while one is in flight. An open
// socket that went up without our certificate is replaced once one quick certificate
// request succeeds, tried at most once a minute; while it still fails the anonymous
// socket is kept, because a receipt that prompts beats one that does not print.
function connectQz() {
    if (_qzConnecting) return _qzConnecting;
    let step;
    if (!qz.websocket.isActive()) {
        step = () => qz.websocket.connect();
    } else if (_qzSocketSigned === false && Date.now() - _qzResignTriedAt >= QZ_RESIGN_INTERVAL_MS) {
        _qzResignTriedAt = Date.now();
        step = () =>
            loadQzCertificate({ attempts: 1 }).then(
                () => qz.websocket.disconnect().then(() => qz.websocket.connect()),
                () => undefined
            );
    } else {
        return Promise.resolve();
    }
    const pending = Promise.resolve()
        .then(step)
        .finally(() => {
            _qzConnecting = null;
        });
    _qzConnecting = pending;
    return pending;
}

// Connect the QZ websocket if not already active. Returns true when connected.
export async function ensureQZConnected() {
    if (!qz?.websocket?.connect) return false;
    try {
        await connectQz();
        return qz.websocket.isActive();
    } catch (e) {
        return false;
    }
}

// Best-effort warm-up so the connection (and any one-time QZ trust prompt on an
// un-configured machine) happens before checkout rather than at print time. Never throws.
export function initQZ() {
    ensureQZConnected().catch(() => {});
}

export async function listKioskPrinters() {
    if (!(await ensureQZConnected()) || !qz?.printers?.find) return [];
    try {
        return (await qz.printers.find()) || [];
    } catch (e) {
        return [];
    }
}

async function resolvePrinter() {
    const configured = getKioskReceiptPrinter();
    if (configured) return configured;
    try {
        const def = await qz.printers.getDefault();
        return def || null;
    } catch (e) {
        return null;
    }
}

// Silently print an HTML document to the configured (or default) printer via QZ Tray.
// Throws on any failure so callers can fall back to browser printing.
export async function printReceiptHtmlViaQZ(html) {
    if (!qz?.websocket || !qz?.configs?.create) {
        throw new Error('QZ Tray not available');
    }
    const connected = await ensureQZConnected();
    if (!connected) throw new Error('QZ Tray not connected');
    const printer = await resolvePrinter();
    if (!printer) throw new Error('No printer available');
    const config = qz.configs.create(printer, { margins: 0 });
    await qz.print(config, [{ type: 'html', format: 'plain', data: html }]);
}

// Expose setup helpers on window for on-device configuration (mirrors the POS console setup).
if (typeof window !== 'undefined') {
    window.setKioskReceiptPrinter = setKioskReceiptPrinter;
    window.getKioskReceiptPrinter = getKioskReceiptPrinter;
    window.listKioskPrinters = listKioskPrinters;
}
